import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider as ReduxProvider } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { HelmetProvider } from "react-helmet-async";
import { store } from "store/actions/store";
import { StatusProvider } from "context/StatusProvider";
import { AlertDialogProvider } from "context/AlertDialogContext";
import { StepUpAuthProvider } from "hooks/auth";
import { ProductAnalyticsConsentSync } from "components/auth/ProductAnalyticsConsentSync";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import "styles/fonts.scss";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "styles/index.scss";
import "styles/globals.css";

import { applyDefaultConsentToGtag, hasAnalyticsConsent } from "lib/analytics/consent";
import { activateProductAnalytics } from "lib/analytics/activate-product-analytics";
import RouteFallback from "routes/RouteFallback";

if (typeof window !== "undefined") {
  // No-op unless REACT_APP_REGIONAL_CONSENT is on and the visitor is outside Europe.
  applyDefaultConsentToGtag();
}

if (typeof window !== "undefined" && hasAnalyticsConsent()) {
  activateProductAnalytics();
}

const isTestEnvironment =
  (typeof window !== "undefined" &&
    window.location.hostname === "localhost" &&
    window.location.port === "3001") ||
  process.env.NODE_ENV === "test" ||
  process.env.PLAYWRIGHT_BASE_URL;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        if (isTestEnvironment) return false;
        if (error?.response?.status >= 400) return false;
        if (!error?.response && failureCount >= 1) return false;
        if (error?.code === "ECONNREFUSED" && failureCount >= 1) return false;
        if (error?.code === "ETIMEDOUT" && failureCount >= 1) return false;
        if (failureCount >= 2) return false;
        return true;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: isTestEnvironment ? 0 : 5 * 60 * 1000,
      cacheTime: isTestEnvironment ? 0 : 10 * 60 * 1000,
    },
    mutations: {
      retry: (failureCount, error: any) => {
        if (isTestEnvironment) return false;
        if (error?.response?.status >= 400) return false;
        if (!error?.response) return false;
        if (error?.code === "ECONNREFUSED") return false;
        if (error?.code === "ETIMEDOUT") return false;
        if (failureCount >= 1) return false;
        return true;
      },
    },
  },
});

if (typeof window !== "undefined") {
  window.__REACT_QUERY_GLOBAL_CLIENT = {
    clear: () => queryClient.clear(),
    getQueryCache: () => queryClient.getQueryCache(),
  };

  const cacheBustFlag = localStorage.getItem("react_query_cache_bust");
  if (cacheBustFlag) {
    queryClient.clear();
    localStorage.removeItem("react_query_cache_bust");
  }
}

const persistor = persistStore(store);

if (typeof window !== "undefined") {
  window.__REDUX_STORE__ = store;
  window.__REDUX_PERSISTOR__ = persistor;
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

const isDevelopment = process.env.NODE_ENV === "development";
const useStrictMode = !isDevelopment;

const AppShell = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <StepUpAuthProvider>
        <StatusProvider>
          <AlertDialogProvider>
            <ProductAnalyticsConsentSync />
            <App />
          </AlertDialogProvider>
        </StatusProvider>
      </StepUpAuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

const renderApp = () => {
  const tree = (
    <ReduxProvider store={store}>
      <PersistGate
        loading={<RouteFallback label="Restoring session…" containerClassName="min-h-screen" />}
        persistor={persistor}
      >
        <AppShell />
      </PersistGate>
    </ReduxProvider>
  );

  if (useStrictMode) {
    return <React.StrictMode>{tree}</React.StrictMode>;
  }

  return tree;
};

root.render(renderApp());
